import { useEffect, useState } from "react";
import { Menu, X, Heart } from "lucide-react";
import { ASSETS, NAV_LINKS } from "../lib/constants";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const close = () => setOpen(false);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-1.5rem)] max-w-7xl transition-all duration-500 ${
        scrolled ? "top-2" : "top-4"
      }`}
    >
      <div className="nav-pill rounded-full px-3 sm:px-4 py-2 flex items-center justify-between gap-3">
        {/* Logo */}
        <a
          href="#home"
          data-testid="header-logo-link"
          className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full hover:bg-white/5 transition-colors"
          aria-label="Semaj J. Morgan Foundation home"
        >
          <span className="w-10 h-10 rounded-full bg-black overflow-hidden grid place-items-center ring-1 ring-white/10">
            <img
              src={ASSETS.foundationLogo}
              alt="Semaj J. Morgan Foundation"
              className="w-9 h-9 object-contain"
            />
          </span>
          <span className="hidden sm:flex flex-col leading-tight">
            <span className="text-white text-sm font-semibold tracking-tight font-display">
              Semaj J. Morgan
            </span>
            <span className="text-white/60 text-[10px] font-medium tracking-[0.18em] uppercase">
              Foundation
            </span>
          </span>
        </a>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="nav-link px-3 py-2 text-sm rounded-full hover:bg-white/5"
            >
              {link.label}
            </a>
          ))}
        </nav>

        {/* CTA + mobile toggle */}
        <div className="flex items-center gap-2">
          <a
            href="#get-involved"
            data-testid="header-get-involved-button"
            className="hidden sm:inline-flex items-center gap-2 bg-[#FFD100] hover:bg-[#FFDB33] text-[#0F172A] font-semibold text-sm px-5 py-2.5 rounded-full transition-all duration-300 hover:-translate-y-0.5 shadow-[0_6px_20px_rgba(255,209,0,0.4)]"
          >
            <Heart className="w-4 h-4" strokeWidth={2.5} fill="currentColor" />
            Get Involved
          </a>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            aria-expanded={open}
            data-testid="header-mobile-menu-toggle"
            className="lg:hidden w-10 h-10 grid place-items-center text-white rounded-full hover:bg-white/10 transition-colors"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="header-mobile-menu"
          className="lg:hidden mt-2 nav-pill rounded-3xl p-4 animate-fade-in"
        >
          <nav className="flex flex-col gap-1">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={close}
                data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="nav-link px-4 py-3 rounded-2xl hover:bg-white/5 text-sm"
              >
                {link.label}
              </a>
            ))}
            <a
              href="#get-involved"
              onClick={close}
              data-testid="mobile-get-involved-button"
              className="mt-2 inline-flex justify-center items-center gap-2 bg-[#FFD100] text-[#0F172A] font-semibold px-5 py-3 rounded-full"
            >
              <Heart className="w-4 h-4" fill="currentColor" /> Get Involved
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};
